import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { getPayrollTherapistList, getPayrollDateDetails, getPayrollItemDetails, createPayrollReportDownload, createPayrollAdjustment, getPayrollAdjustments, updatePayrollAdjustment, deletePayrollAdjustment } from '../../../../APICalling/APICalling';
import { getPeriodText, calculateReportDateRange, isReportRangeWithinMonths } from '../../../../Utils/ReportDateUtils';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import Loader from '../../../../Components/Loader/Loader';
import { toast } from 'react-toastify';
import ReportHeader from '../ReportHeader/ReportHeader';
import Placeholder from '../../../../Components/Placeholder/Placeholder';
import SearchNotFoundIcon from '../../../../Components/Icons/SearchNotFoundIcon';
import styles from './Payroll.module.css';
import PayAdjustmentPopup from './PayAdjustmentPopup';
import DeleteConfirmPopup from '../../../../Components/Popups/DeleteConfirmPopup/DeleteConfirmPopup';
import EditIcon from '../../../../Components/Icons/EditIcon';
import TrashIcon from '../../../../Components/Icons/TrashIcon';
import QuantityCircleAddIcon from '../../../../Components/Icons/QuantityCircleAddIcon';

const PENDING_KEY = 'payroll_download_pending';

const getSaleLinkOutletId = (outletId) => {
	const parsed = Number(outletId);
	return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
};

const formatDateForAdjustmentAPI = (date) => {
	if (!date) return '';
	if (typeof date === 'string') {
		const match = date.match(/^(\d{4}-\d{2}-\d{2})/);
		if (match) return match[1];
	}
	const d = new Date(date);
	if (Number.isNaN(d.getTime())) return date;
	const year = d.getFullYear();
	const month = String(d.getMonth() + 1).padStart(2, '0');
	const day = String(d.getDate()).padStart(2, '0');
	return `${year}-${month}-${day}`;
};

const formatAdjustmentDisplayDate = (dateStr) => {
	if (!dateStr) return 'N/A';

	let d = null;
	if (typeof dateStr === 'string') {
		const sqlMatch = dateStr.match(
			/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
		);
		if (sqlMatch) {
			const [, y, m, day, hh, mm, ss] = sqlMatch;
			d = new Date(
				Number(y),
				Number(m) - 1,
				Number(day),
				Number(hh || 0),
				Number(mm || 0),
				Number(ss || 0)
			);
		}
	}
	if (!d || Number.isNaN(d.getTime())) {
		d = new Date(dateStr);
	}
	if (Number.isNaN(d.getTime())) return dateStr;

	const hasTime = typeof dateStr === 'string' && /\d{2}:\d{2}/.test(dateStr);
	if (!hasTime) {
		return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
	}

	return d.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		hour: 'numeric',
		minute: '2-digit',
		hour12: true,
	});
};

const Payroll = ({ filterParams }) => {
	const { userAssignedOutlets } = useGlobalContext();
	const [payrollData, setPayrollData] = useState([]);
	const [loading, setLoading] = useState(false);
	const [loadingMore, setLoadingMore] = useState(false);
	const [currentPage, setCurrentPage] = useState(1);
	const [hasMore, setHasMore] = useState(true);
	const [outletName, setOutletName] = useState('');
	const [periodText, setPeriodText] = useState('');
	const [expandedTherapist, setExpandedTherapist] = useState(null);
	const [expandedDate, setExpandedDate] = useState(null);
	const [dateDetails, setDateDetails] = useState({});
	const [itemDetails, setItemDetails] = useState({});
	const [adjustmentDetails, setAdjustmentDetails] = useState({});
	const [loadingDetails, setLoadingDetails] = useState({});
	const [downloadLoading, setDownloadLoading] = useState(() => {
		try {
			const pending = sessionStorage.getItem(PENDING_KEY);
			if (pending) {
				const { export_id: exportId } = JSON.parse(pending);
				if (exportId) return true;
			}
		} catch (e) {}
		return false;
	});
	const [adjustmentPopup, setAdjustmentPopup] = useState({
		show: false,
		mode: 'add',
		therapist: null,
		date: '',
		adjustment: null,
	});
	const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
	const [deleteAdjustmentState, setDeleteAdjustmentState] = useState(null);
	const [deleteAdjustmentSubmitting, setDeleteAdjustmentSubmitting] = useState(false);
	const tableWrapperRef = useRef(null);
	const isLoadingRef = useRef(false);
	const observer = useRef();
	const fetchPayrollReportRef = useRef();

	const getEligibleOutlets = useCallback(() => {
		const editableOutlets = (userAssignedOutlets || []).filter((o) => o.mode === 'edit');
		return editableOutlets.length > 0 ? editableOutlets : userAssignedOutlets || [];
	}, [userAssignedOutlets]);

	const getOutletIds = useCallback(
		(value) => {
			if (Array.isArray(value)) return value.map((id) => String(id).trim()).filter(Boolean);
			if (value === undefined || value === null) {
				return getEligibleOutlets().map((outlet) => String(outlet.id));
			}
			const raw = String(value).trim();
			if (!raw) {
				return [];
			}
			return raw.split(',').map((id) => id.trim()).filter(Boolean);
		},
		[getEligibleOutlets]
	);

	// Outlet label for header
	useEffect(() => {
		if (!userAssignedOutlets?.length) return;
		const outletIds = getOutletIds(filterParams?.outlet);
		if (!outletIds.length) {
			setOutletName('Unknown');
			return;
		}
		const eligibleOutlets = getEligibleOutlets();
		const names = outletIds
			.map((id) => eligibleOutlets.find((o) => String(o.id) === String(id))?.name)
			.filter(Boolean);
		setOutletName(names.length ? names.join(', ') : 'Unknown');
	}, [filterParams?.outlet, getEligibleOutlets, getOutletIds, userAssignedOutlets]);

	// Listen for payroll download started event from the WebSocket listener
	useEffect(() => {
		const onDownloadStarted = () => {
			setDownloadLoading(false);
			try {
				sessionStorage.removeItem(PENDING_KEY);
			} catch (e) {}
		};
		window.addEventListener('payroll:download:started', onDownloadStarted);
		return () => window.removeEventListener('payroll:download:started', onDownloadStarted);
	}, []);

	const formatDateForAPI = (date) => {
		// Check if it's already a formatted string with time or simply includes time
		if (typeof date === 'string' && date.includes(' ')) {
			// Try to preserve the time part if it exists
			const parts = date.split(' ');
			const datePart = parts[0]; 
			const timePart = parts[1]; 
			const d = new Date(datePart);
			const day = String(d.getDate()).padStart(2, '0');
			const month = String(d.getMonth() + 1).padStart(2, '0');
			const year = d.getFullYear();
			return `${day}-${month}-${year} ${timePart}`;
		}
		
		const d = new Date(date);
		const day = String(d.getDate()).padStart(2, '0');
		const month = String(d.getMonth() + 1).padStart(2, '0');
		const year = d.getFullYear();
		return `${day}-${month}-${year}`;
	};

	const fetchPayrollReport = useCallback(async (page = 1, isInitialLoad = false) => {
		// Prevent multiple simultaneous calls
		if (isLoadingRef.current) {
			return;
		}

		try {
			isLoadingRef.current = true;
			if (isInitialLoad) {
				setLoading(true);
			} else {
				setLoadingMore(true);
			}

			// Calculate dates based on filter


			// Calculate dates based on filter
			const { startDate, endDate } = calculateReportDateRange(filterParams);

			const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams, 'outlet');
			const outletIds = getOutletIds(filterParams?.outlet);

			const params = {
				start_date: startDate,
				end_date: endDate,
				pagination: 1,
				per_page: 20,
				page: page,
				archive: 1
			};
			if (hasOutletFilter) {
				params.outlet_ids = outletIds.join(',');
			}

			// Add therapist filter if provided
			// Handle empty array (when all therapists are unselected)
			if (filterParams.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					// Format array as JSON string: [42,19,20]
					params.therapist_id = JSON.stringify(filterParams.staff);
				} else {
					// If it's a string or other format, wrap in array and stringify
					params.therapist_id = JSON.stringify([filterParams.staff]);
				}
			}

			if (filterParams.archive === 1) {
				params.archive = 1;
			}

			if (isInitialLoad) {
				setPeriodText(getPeriodText(filterParams));
			}

			const response = await getPayrollTherapistList(params);

			if (response?.data?.success) {
				const responseData = response.data.data || {};
				const dataList = responseData.list || [];
				const pagination = responseData.pagination || {};

				if (isInitialLoad) {
					setPayrollData(dataList);
				} else {
					setPayrollData(prev => [...prev, ...dataList]);
				}

				// Update pagination state
				const currentPageNum = pagination.currentPage || page;
				const lastPage = pagination.lastPage || 1;
				
				setCurrentPage(currentPageNum);
				setHasMore(currentPageNum < lastPage);
			} else {
				if (isInitialLoad) {
					toast.error(response?.response?.data?.message || response?.data?.message || response?.message || 'Failed to fetch payroll report');
					setPayrollData([]);
				}
			}
		} catch (error) {
			console.error('Error fetching payroll report:', error);
			if (isInitialLoad) {
				toast.error('An error occurred while fetching payroll report');
				setPayrollData([]);
			}
		} finally {
			isLoadingRef.current = false;
			setLoading(false);
			setLoadingMore(false);
		}
	}, [filterParams, getOutletIds]);

	// Store fetchPayrollReport in ref for use in observer
	useEffect(() => {
		fetchPayrollReportRef.current = fetchPayrollReport;
	}, [fetchPayrollReport]);

	// Initial load when filterParams change (Generate)
	useEffect(() => {
		if (filterParams && filterParams.date) {
			setCurrentPage(1);
			setHasMore(true);
			setPayrollData([]);
			setExpandedTherapist(null);
			setExpandedDate(null);
			setDateDetails({});
			setItemDetails({});
			setAdjustmentDetails({});
			fetchPayrollReport(1, true);
		}
	}, [filterParams, fetchPayrollReport]);

	// Intersection Observer for infinite scroll
	const lastItemRef = useCallback(node => {
		if (observer.current) observer.current.disconnect();
		
		if (loading || loadingMore || !hasMore || !node) {
			return;
		}
		
		observer.current = new IntersectionObserver(entries => {
			if (entries[0].isIntersecting && hasMore && !isLoadingRef.current && !loading && !loadingMore) {
				const nextPage = currentPage + 1;
				if (fetchPayrollReportRef.current) {
					fetchPayrollReportRef.current(nextPage, false);
				}
			}
		}, {
			root: null,
			rootMargin: '100px',
			threshold: 0.1
		});
		
		observer.current.observe(node);
	}, [loading, loadingMore, hasMore, currentPage]);

	const fetchDateDetailsForTherapist = async (therapist, { force = false } = {}) => {
		const therapistKey = therapist.user_id;
		if (!force && dateDetails[therapistKey]) {
			return dateDetails[therapistKey];
		}

		const { startDate, endDate } = calculateReportDateRange(filterParams);
		const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams, 'outlet');
		const outletIds = getOutletIds(filterParams?.outlet);

		const params = {
			start_date: startDate,
			end_date: endDate,
			user_id: therapist.user_id,
			therapist: therapist.user_id,
			per_page: 20
		};
		if (hasOutletFilter) {
			params.outlet_ids = outletIds.join(',');
		}

		const response = await getPayrollDateDetails(params);
		if (response?.data?.success) {
			const list = response.data.list || [];
			setDateDetails(prev => ({
				...prev,
				[therapistKey]: list
			}));
			return list;
		}
		return null;
	};

	const handleTherapistClick = async (therapist) => {
		const therapistKey = therapist.user_id;

		if (expandedTherapist === therapistKey) {
			setExpandedTherapist(null);
			setExpandedDate(null);
			return;
		}

		setExpandedTherapist(therapistKey);
		setExpandedDate(null);

		if (dateDetails[therapistKey]) {
			return;
		}

		try {
			setLoadingDetails(prev => ({ ...prev, [therapistKey]: true }));
			await fetchDateDetailsForTherapist(therapist);
		} catch (error) {
			console.error('Error fetching date details:', error);
			toast.error('Failed to fetch day details');
		} finally {
			setLoadingDetails(prev => ({ ...prev, [therapistKey]: false }));
		}
	};

	const fetchAdjustmentsForDate = async (therapist, dateItem, { force = false } = {}) => {
		const dateKey = `${therapist.user_id}-${dateItem.date}`;
		if (!force && adjustmentDetails[dateKey]) {
			return adjustmentDetails[dateKey];
		}

		// Use same start/end (including times) as the main payroll APIs
		const { startDate, endDate } = calculateReportDateRange(filterParams);
		const response = await getPayrollAdjustments({
			therapist_id: therapist.user_id,
			start_date: startDate,
			end_date: endDate,
		});

		const list = response?.data?.data || response?.data || [];
		const allList = Array.isArray(list) ? list : [];
		const dayOnly = formatDateForAdjustmentAPI(dateItem.date);
		const dayList = allList.filter(
			(adjustment) => formatDateForAdjustmentAPI(adjustment?.date) === dayOnly
		);

		setAdjustmentDetails(prev => ({
			...prev,
			[dateKey]: dayList,
		}));
		return dayList;
	};

	const fetchItemDetailsForDate = async (therapist, dateItem) => {
		const dateKey = `${therapist.user_id}-${dateItem.date}`;
		const formattedDate = formatDateForAPI(dateItem.date);
		const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams, 'outlet');
		const outletIds = getOutletIds(filterParams?.outlet);

		const params = {
			start_date: formattedDate,
			end_date: formattedDate,
			user_id: therapist.user_id,
			therapist: therapist.user_id,
			per_page: 20
		};
		if (hasOutletFilter) {
			params.outlet_ids = outletIds.join(',');
		}

		const response = await getPayrollItemDetails(params);
		if (response?.data?.success) {
			setItemDetails(prev => ({
				...prev,
				[dateKey]: {
					list: response.data.list || [],
					outlet_name: response.data.outlet_name || ''
				}
			}));
		}
	};

	const isAdjustmentDateInSelectedRange = (dateValue) => {
		if (!dateValue || !filterParams) return false;

		const { startDate, endDate } = calculateReportDateRange(filterParams);
		if (!startDate || !endDate) return false;

		const parseBoundary = (value, isEnd) => {
			if (typeof value !== 'string') return null;
			const match = value.match(
				/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2})(?::(\d{2}))?)?/
			);
			if (!match) return null;
			const [, day, month, year, hh, mm, ss] = match;
			return new Date(Number(year),Number(month) - 1,Number(day),Number(hh ?? (isEnd ? 23 : 0)),Number(mm ?? (isEnd ? 59 : 0)),Number(ss ?? (isEnd ? 59 : 0)));
		};

		const parseAdjustmentDate = (value) => {
			if (typeof value === 'string') {
				const sqlMatch = value.match(
					/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?/
				);
				if (sqlMatch) {
					const [, y, m, d, hh, mm, ss] = sqlMatch;
					return new Date(Number(y),Number(m) - 1,Number(d),Number(hh || 0),Number(mm || 0),Number(ss || 0));
				}
			}
			const parsed = new Date(value);
			return Number.isNaN(parsed.getTime()) ? null : parsed;
		};

		const adjustmentDate = parseAdjustmentDate(dateValue);
		const rangeStart = parseBoundary(startDate, false);
		const rangeEnd = parseBoundary(endDate, true);
		if (!adjustmentDate || !rangeStart || !rangeEnd) return false;

		return adjustmentDate >= rangeStart && adjustmentDate <= rangeEnd;
	};

	const findDateItemInList = (list, adjustmentDate) => {
		if (!Array.isArray(list) || !adjustmentDate) return null;
		const dayOnly = formatDateForAdjustmentAPI(adjustmentDate);
		return list.find((dayItem) => formatDateForAdjustmentAPI(dayItem.date) === dayOnly) || null;
	};

	const applyTherapistPayrollDelta = (therapist, amountDelta) => {
		if (!amountDelta) return;
		setPayrollData(prev =>
			prev.map(item => {
				if (item.user_id !== therapist.user_id) return item;
				return {
					...item,
					pay_adjustments: parseFloat(item.pay_adjustments || 0) + amountDelta,
					total_price: parseFloat(item.total_price || 0) + amountDelta,
				};
			})
		);
	};

	const handleOpenAdjustmentPopup = (e, therapist, dateItem) => {
		e.stopPropagation();
		setAdjustmentPopup({
			show: true,
			mode: 'add',
			therapist,
			date: formatDateForAdjustmentAPI(dateItem.date),
			adjustment: null,
		});
	};

	const handleOpenEditAdjustmentPopup = (e, therapist, dateItem, adjustment) => {
		e.stopPropagation();
		setAdjustmentPopup({
			show: true,
			mode: 'edit',
			therapist,
			date: formatDateForAdjustmentAPI(dateItem.date),
			adjustment,
		});
	};

	const handleCloseAdjustmentPopup = () => {
		if (adjustmentSubmitting) return;
		setAdjustmentPopup({
			show: false,
			mode: 'add',
			therapist: null,
			date: '',
			adjustment: null,
		});
	};

	const handleSubmitAdjustment = async (formData) => {
		const { therapist, mode, adjustment, date } = adjustmentPopup;
		if (!therapist) return;

		const openedDateItem = { date: date || adjustment?.date };
		const submittedDate = formData.date || openedDateItem.date;

		setAdjustmentSubmitting(true);
		try {
			if (mode === 'edit') {
				const previousAmount = parseFloat(adjustment?.amount || 0);
				const previousDate = adjustment?.date || date;
				const previousInRange = isAdjustmentDateInSelectedRange(previousDate);
				const nextInRange = isAdjustmentDateInSelectedRange(submittedDate);

				const payload = {
					therapist_id: therapist.user_id,
					date: formData.date,
					type: formData.type,
					amount: formData.amount,
					description: formData.description || undefined,
				};

				const response = await updatePayrollAdjustment(formData.id, payload);
				const isSuccess = response?.success || response?.data?.success;

				if (isSuccess) {
					toast.success('Pay adjustment updated successfully');
					setAdjustmentPopup({
						show: false,
						mode: 'add',
						therapist: null,
						date: '',
						adjustment: null,
					});

					let mainTotalDelta = 0;
					if (previousInRange && nextInRange) {
						mainTotalDelta = formData.amount - previousAmount;
					} else if (previousInRange && !nextInRange) {
						mainTotalDelta = -previousAmount;
					} else if (!previousInRange && nextInRange) {
						mainTotalDelta = formData.amount;
					}
					applyTherapistPayrollDelta(therapist, mainTotalDelta);

					const refreshedDayList = await fetchDateDetailsForTherapist(therapist, { force: true });

					const previousDayOnly = formatDateForAdjustmentAPI(previousDate);
					const nextDayOnly = formatDateForAdjustmentAPI(submittedDate);
					const nextDateItem =
						findDateItemInList(refreshedDayList, submittedDate) ||
						(nextInRange ? { date: nextDayOnly } : null);
					const previousDateItem =
						findDateItemInList(refreshedDayList, previousDate) ||
						(previousInRange ? { date: previousDayOnly } : null);

					const refreshTasks = [];
					if (nextDateItem) {
						refreshTasks.push(fetchAdjustmentsForDate(therapist, nextDateItem, { force: true }));
					}
					if (previousDateItem && previousDayOnly !== nextDayOnly) {
						refreshTasks.push(fetchAdjustmentsForDate(therapist, previousDateItem, { force: true }));
					}
					if (openedDateItem.date) {
						const openedDayOnly = formatDateForAdjustmentAPI(openedDateItem.date);
						if (
							openedDayOnly !== nextDayOnly &&
							openedDayOnly !== previousDayOnly
						) {
							const openedDateRow =
								findDateItemInList(refreshedDayList, openedDateItem.date) || openedDateItem;
							refreshTasks.push(fetchAdjustmentsForDate(therapist, openedDateRow, { force: true }));
						}
					}
					await Promise.all(refreshTasks);
					return;
				}

				toast.error(response?.message || response?.data?.message || 'Failed to update pay adjustment');
				return;
			}

			const payload = {
				therapist_id: therapist.user_id,
				date: formData.date,
				type: formData.type,
				amount: formData.amount,
				description: formData.description || undefined,
			};

			const response = await createPayrollAdjustment(payload);
			const isSuccess = response?.success || response?.data?.success;

			if (isSuccess) {
				toast.success('Pay adjustment added successfully');
				setAdjustmentPopup({
					show: false,
					mode: 'add',
					therapist: null,
					date: '',
					adjustment: null,
				});

				const submittedInRange = isAdjustmentDateInSelectedRange(submittedDate);
				const submittedDayOnly = formatDateForAdjustmentAPI(submittedDate);

				// Main Pay Adjustments total only when the adjustment date is in the selected range
				if (submittedInRange) {
					applyTherapistPayrollDelta(therapist, formData.amount);
				}

				const refreshedDayList = await fetchDateDetailsForTherapist(therapist, { force: true });

				const refreshTasks = [];
				const resolvedSubmittedDateItem = findDateItemInList(refreshedDayList, submittedDate);
				if (resolvedSubmittedDateItem) {
					refreshTasks.push(
						fetchAdjustmentsForDate(therapist, resolvedSubmittedDateItem, { force: true })
					);
				} else if (submittedInRange && submittedDayOnly) {
					// Day may not appear yet if API day list is empty; still key cache by submitted day
					refreshTasks.push(
						fetchAdjustmentsForDate(therapist, { date: submittedDayOnly }, { force: true })
					);
				}

				const openedDayOnly = formatDateForAdjustmentAPI(openedDateItem.date);
				if (openedDayOnly && openedDayOnly !== submittedDayOnly) {
					const openedDateRow =
						findDateItemInList(refreshedDayList, openedDateItem.date) || openedDateItem;
					refreshTasks.push(fetchAdjustmentsForDate(therapist, openedDateRow, { force: true }));
				}

				await Promise.all(refreshTasks);
				return;
			}

			toast.error(response?.message || response?.data?.message || 'Failed to add pay adjustment');
		} catch (error) {
			console.error('Error saving pay adjustment:', error);
			toast.error(`An error occurred while ${mode === 'edit' ? 'updating' : 'adding'} pay adjustment`);
		} finally {
			setAdjustmentSubmitting(false);
		}
	};

	const handleOpenDeleteAdjustment = (e, therapist, dateItem, adjustment) => {
		e.stopPropagation();
		setDeleteAdjustmentState({ therapist, dateItem, adjustment });
	};

	const handleCloseDeleteAdjustment = () => {
		if (deleteAdjustmentSubmitting) return;
		setDeleteAdjustmentState(null);
	};

	const handleConfirmDeleteAdjustment = async () => {
		const { therapist, dateItem, adjustment } = deleteAdjustmentState || {};
		if (!therapist || !dateItem || !adjustment?.id) return;

		setDeleteAdjustmentSubmitting(true);
		try {
			const response = await deletePayrollAdjustment(adjustment.id);
			const isSuccess = response?.success || response?.data?.success;

			if (isSuccess) {
				toast.success('Pay adjustment deleted.');
				setDeleteAdjustmentState(null);

				const removedAmount = parseFloat(adjustment.amount || 0);
				const adjustmentDate = adjustment.date || dateItem.date;
				const inSelectedRange = isAdjustmentDateInSelectedRange(adjustmentDate);

				if (inSelectedRange) {
					applyTherapistPayrollDelta(therapist, -removedAmount);
				}

				const refreshedDayList = await fetchDateDetailsForTherapist(therapist, { force: true });
				const resolvedDateItem =
					findDateItemInList(refreshedDayList, adjustmentDate) ||
					findDateItemInList(refreshedDayList, dateItem.date) ||
					dateItem;
				await fetchAdjustmentsForDate(therapist, resolvedDateItem, { force: true });
				return;
			}

			toast.error(response?.message || response?.data?.message || 'Failed to delete pay adjustment');
		} catch (error) {
			console.error('Error deleting pay adjustment:', error);
			toast.error('An error occurred while deleting pay adjustment');
		} finally {
			setDeleteAdjustmentSubmitting(false);
		}
	};

	const handleDateClick = async (therapist, dateItem) => {
		const dateKey = `${therapist.user_id}-${dateItem.date}`;
		
		if (expandedDate === dateKey) {
			setExpandedDate(null);
			return;
		}

		setExpandedDate(dateKey);

		const needsItems = !itemDetails[dateKey];
		const needsAdjustments = !adjustmentDetails[dateKey];
		if (!needsItems && !needsAdjustments) {
			return;
		}

		try {
			setLoadingDetails(prev => ({ ...prev, [dateKey]: true }));
			await Promise.all([
				needsItems ? fetchItemDetailsForDate(therapist, dateItem) : Promise.resolve(),
				needsAdjustments ? fetchAdjustmentsForDate(therapist, dateItem) : Promise.resolve(),
			]);
		} catch (error) {
			console.error('Error fetching date details:', error);
			toast.error('Failed to fetch day details');
		} finally {
			setLoadingDetails(prev => ({ ...prev, [dateKey]: false }));
		}
	};

	const formatCurrency = (value) => {
		const num = parseFloat(value || 0);
		const amount = Math.abs(num).toFixed(2);
		return num < 0 ? `-$ ${amount}` : `$ ${amount}`;
	};

	const formatRate = (value) => {
		if (value === '-' || value === null || value === undefined || value === '') {
			return '-';
		}
		const num = parseFloat(value);
		if (Number.isNaN(num)) {
			return '-';
		}
		return formatCurrency(value);
	};

	const formatHours = (minutesValue, format = 'decimal') => {
		if (minutesValue === '-' || minutesValue === null || minutesValue === undefined) {
			return '-';
		}
		const mins = parseFloat(minutesValue);
		if (Number.isNaN(mins)) {
			return '-';
		}
		if (format === 'hm') {
			const totalMinutes = Math.round(mins);
			const hours = Math.floor(totalMinutes / 60);
			const minutes = totalMinutes % 60;
			return `${hours}h ${minutes}m`;
		}
		return (mins / 60).toFixed(2);
	};

	const sortedPayrollData = useMemo(() => {
		return [...payrollData].sort((a, b) =>
			String(a?.user_name || '').localeCompare(String(b?.user_name || ''), undefined, {
				sensitivity: 'base'
			})
		);
	}, [payrollData]);

	// Handle sale ID click - redirect to sales page with search
	const handleSaleClick = (saleId, outletId) => {
		// Extract numeric sale id (e.g. "sale#267" -> "267")
		const cleanSaleIdMatch = String(saleId || '').match(/\d+/);
		const cleanSaleId = cleanSaleIdMatch ? cleanSaleIdMatch[0] : '';
		if (!cleanSaleId) return;
		const outletForLink = getSaleLinkOutletId(outletId);
		const url = `/sales?search=${cleanSaleId}&outlet=${outletForLink}`;
		window.open(url, "_blank");
	};

	const handleDownload = async () => {
		if (!payrollData || payrollData.length === 0) {
			toast.warning('No data available to download');
			return;
		}
		if (!isReportRangeWithinMonths(filterParams, 3)) {
			toast.error('Please select a date range within 90 days to proceed.');
			return;
		}

		// Get user_id and token from localStorage
		const userId = localStorage.getItem('user_id');
		const token = localStorage.getItem('token');
		
		if (!userId) {
			toast.error('User ID not found');
			return;
		}

		if (!token) {
			toast.error('Authentication token not found');
			return;
		}

		setDownloadLoading(true);

		try {
			toast.info('We’ll notify you when the payroll report is ready.');

			// Calculate dates for export
			const { startDate, endDate } = calculateReportDateRange(filterParams);

			// Call create API to trigger report generation (same payload as before)
			const hasOutletFilter = Object.prototype.hasOwnProperty.call(filterParams || {}, 'outlet');
			const outletIds = getOutletIds(filterParams?.outlet);

			const downloadParams = {
				start_date: startDate,
				end_date: endDate,
				user_id: userId
			};
			if (hasOutletFilter) {
				downloadParams.outlet_ids = outletIds.join(',');
			}

			// Add therapist filter if provided
			// Handle empty array (when all therapists are unselected)
			if (filterParams.staff !== undefined) {
				if (Array.isArray(filterParams.staff)) {
					// Format array as JSON string: [42,19,20]
					downloadParams.therapist_id = JSON.stringify(filterParams.staff);
				} else {
					// If it's a string or other format, wrap in array and stringify
					downloadParams.therapist_id = JSON.stringify([filterParams.staff]);
				}
			}

			if (filterParams.archive === 1) {
				downloadParams.archive = 1;
			}

			const createResponse = await createPayrollReportDownload(downloadParams);
			const responseExportId =
				createResponse?.data?.export_id ||
				createResponse?.data?.data?.export_id ||
				createResponse?.export_id ||
				'';

			const isSuccess = createResponse?.data?.data?.success || 
							  createResponse?.data?.success || 
							  createResponse?.success;
			
			if (isSuccess) {
				if (!responseExportId) {
					toast.error('Export ID was not received. Please try again.');
					setDownloadLoading(false);
					return;
				}
				// Keep downloadLoading=true — it will be cleared when the WS event fires
				try {
					sessionStorage.setItem(
						PENDING_KEY,
						JSON.stringify({ export_id: responseExportId })
					);
				} catch (e) {}
				return; // Notification will be shown when socket event arrives (global listener)
			}

			// If not successful, handle error
			if (!isSuccess) {
				const backendError = createResponse?.data?.data?.message ||
								   createResponse?.data?.message || 
								   createResponse?.message || 
								   'Failed to initiate download';
				
				// Show user-friendly error message
				let userMessage = 'Failed to generate report. ';
				
				if (backendError.includes('S3') || backendError.includes('AWS') || backendError.includes('storage')) {
					userMessage += 'Server storage configuration issue. Please contact support.';
				} else if (backendError.includes('ACL')) {
					userMessage += 'File permission error. Please contact support.';
				} else {
					userMessage = backendError.substring(0, 200);
				}
				
				toast.error(userMessage, {
					autoClose: 6000
				});

				setDownloadLoading(false);
				return;
			}

		} catch (error) {
			const errorMessage = error?.response?.data?.message || 
							   error?.message || 
							   'An error occurred while preparing download';
			
			toast.error(errorMessage, {
				autoClose: 5000
			});

			setDownloadLoading(false);
		}
	};

	return (
		<div className={styles.payrollContainer}>
			{/* Header Section */}
			<div className={styles.reportHeaderWrapper}>
				<ReportHeader
					outletName={outletName}
					periodText={periodText}
					onDownload={handleDownload}
					downloadLoading={downloadLoading}
					hasData={payrollData && payrollData.length > 0}
					downloadButtonText="Download CSV"
				/>
			</div>

			{/* Table Section */}
			{loading ? (
				<div className={styles.loaderContainer}>
					<Loader />
				</div>
			) : sortedPayrollData.length > 0 ? (
				<div className={styles.tableWrapper} ref={tableWrapperRef}>
					<table className={styles.payrollTable}>
						<thead style={{ position: 'sticky', top: 0, zIndex: 10, }}>
							<tr>
								<th className={styles.colWorkName}>Work Name</th>
								<th className={styles.colTotal}>Total</th>
								<th className={styles.colHours}>Hours</th>
								<th className={styles.colInStoreHours}>In Store Hrs.</th>
								{/* <th className={styles.colBlocked}>Blocked</th> */}
								<th className={styles.colServices}>Services</th>
								<th className={styles.colServiceSales}>Service Sales</th>
								<th className={styles.colServiceSales}>Addon Sales</th>
								<th className={styles.colServiceComp}>Service Comm.</th>
								<th className={styles.colServiceComp}>Addon Comm.</th>
								<th className={styles.colPayAdjustments}>Pay Adjustments</th>
								{/* <th className={styles.colTips}>Tips</th> */}
								<th className={styles.colOutlet}>Outlet</th>
							</tr>
						</thead>
						<tbody>
							{sortedPayrollData.map((therapist, index) => (
								<React.Fragment key={therapist.user_id}>
									{/* Main therapist row */}
									<tr 
										className={`${styles.therapistRow} ${expandedTherapist === therapist.user_id ? styles.expanded : ''}`}
										onClick={() => handleTherapistClick(therapist)}
									>
										<td className={styles.colWorkName} title={therapist.user_name || 'N/A'}>{therapist.user_name || 'N/A'}</td>
										<td className={styles.colTotal}>{formatCurrency(therapist.total_price)}</td>
										<td className={styles.colHours}>{formatHours(therapist.total_duration)}</td>
										<td className={styles.colInStoreHours}>{formatHours(therapist.in_store_hours, 'hm')}</td>
										{/* <td className={styles.colBlocked}>{therapist.blocked || 0}</td> */}
										<td className={styles.colServices}>{therapist.total_service || 0}</td>
										<td className={styles.colServiceSales}>{formatCurrency(therapist.service_sales)}</td>
										<td className={styles.colServiceSales}>{formatCurrency(therapist.addon_sales || 0)}</td>
										<td className={styles.colServiceComp}>{formatCurrency(therapist.service_comp)}</td>
										<td className={styles.colServiceComp}>{formatCurrency(therapist.addon_commission || 0)}</td>
										<td className={styles.colPayAdjustments}>{formatCurrency(therapist.pay_adjustments || 0)}</td>
										{/* <td className={styles.colTips}>{formatCurrency(therapist.tip || 0)}</td> */}
										<td className={styles.colOutlet} title={therapist.outlet || '—'}>{therapist.outlet || '—'}</td>
									</tr>

									{/* Expanded date details */}
									{expandedTherapist === therapist.user_id && (
										<tr className={styles.expandedRow}>
											<td colSpan="11">
												<div className={styles.dayDetailsContainer}>
													<h3 className={styles.dayDetailsTitle}>Day Details</h3>
													
													{loadingDetails[therapist.user_id] ? (
														<div className={styles.loaderSmall}>
															<Loader />
														</div>
													) : dateDetails[therapist.user_id] && dateDetails[therapist.user_id].length > 0 ? (
														<table className={styles.dayDetailsTable}>
															<thead>
																<tr>
																	<th className={styles.colDayDate}>Date</th>
																	<th className={styles.colTotal}>Total</th>
																	<th className={styles.colHours}>Hours</th>
																	<th className={styles.colInStoreHours}>In Store Hrs.</th>
																	{/* <th>Blocked</th> */}
																	<th className={styles.colServices}>Services</th>
																	<th className={styles.colServiceSales}>Service Sales</th>
																	<th className={styles.colServiceSales}>Addon Sales</th>
																	<th className={styles.colServiceComp}>Service Comm.</th>
																	<th className={styles.colServiceComp}>Addon Comm.</th>
																	<th className={styles.colPayAdjustmentsDetail}>Pay Adjustments</th>
																	{/* <th>Tips</th> */}
																</tr>
															</thead>
															<tbody>
																{dateDetails[therapist.user_id].map((dateItem) => (
																	<React.Fragment key={dateItem.date}>
																		{/* Date row */}
																		<tr 
																			className={`${styles.dateRow} ${expandedDate === `${therapist.user_id}-${dateItem.date}` ? styles.expanded : ''}`}
																			onClick={(e) => {
																				e.stopPropagation();
																				handleDateClick(therapist, dateItem);
																			}}
																		>
																			<td className={styles.colDayDate}>
																				{(() => {
																					const d = new Date(dateItem.date);
																					const month = d.toLocaleDateString('en-US', { month: 'short' });
																					const day = d.getDate();
																					const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
																					return `${month} ${day} (${weekday})`;
																				})()}
																			</td>
																			<td className={styles.colTotal}>{formatCurrency(dateItem.total_price)}</td>
																			<td className={styles.colHours}>{formatHours(dateItem.total_duration)}</td>
																			<td className={styles.colInStoreHours}>{formatHours(dateItem.in_store_hours, 'hm')}</td>
																			{/* <td>{dateItem.blocked || 0}</td> */}
																			<td className={styles.colServices}>{dateItem.total_service || 0}</td>
																			<td className={styles.colServiceSales}>{formatCurrency(dateItem.service_sales)}</td>
																			<td className={styles.colServiceSales}>{formatCurrency(dateItem.addon_sales || 0)}</td>
																			<td className={styles.colServiceComp}>{formatCurrency(dateItem.service_comp)}</td>
																			<td className={styles.colServiceComp}>{formatCurrency(dateItem.addon_commission || 0)}</td>
																			<td className={styles.colPayAdjustmentsDetail}>
																				<div className={styles.payAdjustmentCell}>
																					<button
																						type="button"
																						className={styles.addAdjustmentBtn}
																						onClick={(e) => handleOpenAdjustmentPopup(e, therapist, dateItem)}
																						title="Add pay adjustment"
																						aria-label="Add pay adjustment"
																					>
																						+
																					</button>
																					<span>{formatCurrency(dateItem.pay_adjustments || 0)}</span>
																				</div>
																			</td>
																			{/* <td className={styles.colTips}>{formatCurrency(dateItem.tip || 0)}</td> */}
																		</tr>

																		{/* Expanded service details */}
																		{expandedDate === `${therapist.user_id}-${dateItem.date}` && (
																			<tr className={styles.expandedServiceRow}>
																				<td colSpan="10">
																					<div className={styles.serviceDetailsContainer}>
																						<h4 className={styles.serviceDetailsTitle}>
																							Service & product commission
																							{/* {itemDetails[`${therapist.user_id}-${dateItem.date}`]?.outlet_name ? (
																								<span className={styles.serviceDetailsOutlet}>
																									{' '}
																									— {itemDetails[`${therapist.user_id}-${dateItem.date}`].outlet_name}
																								</span>
																							) : null} */}
																						</h4>
																						
																						{loadingDetails[`${therapist.user_id}-${dateItem.date}`] ? (
																							<div className={styles.loaderSmall}>
																								<Loader />
																							</div>
																						) : itemDetails[`${therapist.user_id}-${dateItem.date}`]?.list?.length > 0 ? (
																							<table className={styles.serviceDetailsTable}>
																								<thead>
																									<tr>
																										<th className={styles.colSaleId}>Sale #</th>
																										<th className={styles.colDate}>Date</th>
																										<th className={styles.colClient}>Client</th>
																										<th className={styles.colType}>Type</th>
																										<th className={styles.colServiceName}>Service</th>
																										<th className={styles.colRevenue}>Revenue</th>
																										<th className={styles.colRate}>Rate</th>
																										<th className={styles.colCommission}>Commission</th>
																									</tr>
																								</thead>
																								<tbody>
																									{itemDetails[`${therapist.user_id}-${dateItem.date}`].list.map((item, idx) => (
																										<tr key={idx}>
																											<td className={styles.colSaleId}>
																												{item.sales_id ? (
																													<span 
																														className={styles.clickableSaleId}
																														onClick={() => handleSaleClick(item.sales_id, item.outlet_id ?? therapist.outlet_id)}
																														title="Click to view sale details"
																													>
																														{item.sales_id}
																													</span>
																												) : (
																													'N/A'
																												)}
																											</td>
																											<td className={styles.colDate}>{item.date || 'N/A'}</td>
																											<td className={styles.colClient}>{item.client_name || 'N/A'}</td>
																											<td className={styles.colType}>{item.type || 'N/A'}</td>
																											<td className={styles.colServiceName}>{item.service || 'N/A'}</td>
																											<td className={styles.colRevenue}>{formatCurrency(item.revenue)}</td>
																											<td className={styles.colRate}>{formatRate(item.rate)}</td>
																											<td className={styles.colCommission}>{formatCurrency(item.commission)}</td>
																										</tr>
																									))}
																								</tbody>
																							</table>
																						) : (
																							<p className={styles.sectionNoData}>No service details available</p>
																						)}

																						<h4 className={styles.adjustmentsTitle}>Adjustments</h4>

																						{loadingDetails[`${therapist.user_id}-${dateItem.date}`] &&
																						!adjustmentDetails[`${therapist.user_id}-${dateItem.date}`] ? (
																							<div className={styles.loaderSmall}>
																								<Loader />
																							</div>
																						) : adjustmentDetails[`${therapist.user_id}-${dateItem.date}`]?.length > 0 ? (
																							<table className={styles.adjustmentsTable}>
																								<thead>
																									<tr>
																										<th className={styles.colAdjDate}>Date & Time</th>
																										<th className={styles.colAdjType}>Type</th>
																										<th className={styles.colAdjDescription}>Description</th>
																										<th className={styles.colAdjAmount}>Amount</th>
																										<th className={styles.colAdjActions}>Actions</th>
																									</tr>
																								</thead>
																								<tbody>
																									{adjustmentDetails[`${therapist.user_id}-${dateItem.date}`].map((adjustment) => (
																										<tr key={adjustment.id}>
																											<td className={styles.colAdjDate}>
																												{formatAdjustmentDisplayDate(adjustment.date)}
																											</td>
																											<td className={styles.colAdjType}>{adjustment.type || 'N/A'}</td>
																											<td className={styles.colAdjDescription} title={adjustment.description || ''}>
																												{adjustment.description || '—'}
																											</td>
																											<td className={styles.colAdjAmount}>
																												{formatCurrency(adjustment.amount)}
																											</td>
																											<td className={styles.colAdjActions}>
																												<div className={styles.adjustmentActions}>
																													<button
																														type="button"
																														className={styles.actionButton}
																														onClick={(e) => handleOpenEditAdjustmentPopup(e, therapist, dateItem, adjustment)}
																														title="Edit adjustment"
																														aria-label="Edit adjustment"
																													>
																														<EditIcon size={14} color="#3C2212" />
																													</button>
																													<button
																														type="button"
																														className={styles.actionButton}
																														onClick={(e) => handleOpenDeleteAdjustment(e, therapist, dateItem, adjustment)}
																														title="Delete adjustment"
																														aria-label="Delete adjustment"
																													>
																														<TrashIcon width={16} height={16} color="#767676" />
																													</button>
																												</div>
																											</td>
																										</tr>
																									))}
																								</tbody>
																							</table>
																						) : (
																							<button
																								type="button"
																								className={styles.addAdjustmentEmptyAction}
																								onClick={(e) => handleOpenAdjustmentPopup(e, therapist, dateItem)}
																							>
																								<QuantityCircleAddIcon size={18} color="#767676" />
																								<span>Add pay adjustment</span>
																							</button>
																						)}
																					</div>
																				</td>
																			</tr>
																		)}
																	</React.Fragment>
																))}
															</tbody>
														</table>
													) : (
														<p className={styles.sectionNoData}>No day details available</p>
													)}
												</div>
											</td>
										</tr>
									)}
								</React.Fragment>
							))}
							{/* Sentinel element for infinite scroll */}
							{hasMore && (
								<tr>
									<td colSpan="11" ref={lastItemRef} style={{ height: '1px', padding: 0 }}></td>
								</tr>
							)}
						</tbody>
					</table>
					{/* {!hasMore && payrollData.length > 0 && (
						<div className={styles.noMoreData}>
							<p>No more data to load</p>
						</div>
					)} */}
				</div>
			) : (
				<div className={styles.noData}>
					<Placeholder
						icon={<SearchNotFoundIcon width="24" height="24" color="white"/>}
						heading="No payroll data found"
						para="Please try changing the filters"
					/>
				</div>
			)}

			<PayAdjustmentPopup
				show={adjustmentPopup.show}
				onClose={handleCloseAdjustmentPopup}
				onSubmit={handleSubmitAdjustment}
				date={adjustmentPopup.date}
				mode={adjustmentPopup.mode}
				adjustment={adjustmentPopup.adjustment}
				isProcessing={adjustmentSubmitting}
			/>

			{deleteAdjustmentState && (
				<DeleteConfirmPopup
					title="Delete Pay Adjustment"
					message="Are you sure you want to delete this pay adjustment? This action cannot be undone."
					onCancel={handleCloseDeleteAdjustment}
					onConfirm={handleConfirmDeleteAdjustment}
					isDeleting={deleteAdjustmentSubmitting}
				/>
			)}
		</div>
	);
};

export default Payroll;
